import type { Change } from 'diff';

/**
 * Represents a group of consecutive changes of the same type in a diff.
 * Groups are used to enable collapsible sections for better diff readability.
 */
export interface ChangeGroup {
  /** The type of changes in this group - either 'changed' (added/removed) or 'unchanged' */
  type: 'changed' | 'unchanged';
  /** Array of Change objects that belong to this group */
  changes: Change[];
  /** Starting line number for this group (1-based) */
  startLine: number;
  /** Ending line number for this group (1-based) */
  endLine: number;
  /** Whether this group can be collapsed (only applies to unchanged groups) */
  isCollapsible?: boolean;
  /** Whether this group is currently expanded (only applies to collapsible groups) */
  isExpanded?: boolean;
}

export type CollapseExpandPosition = 'above' | 'below' | 'middle';

export type CollapsePlanStep =
  | { kind: 'render'; changes: Change[]; startLine: number }
  | { kind: 'expand'; hiddenCount: number; position: CollapseExpandPosition; groupIndex: number };

/**
 * Groups consecutive changes of the same type into ChangeGroup objects.
 * This enables collapsible sections for unchanged content to improve diff readability.
 */
export function groupChangesForCollapse(diff: Change[], contextLines: number): ChangeGroup[] {
  const groups: ChangeGroup[] = [];
  let currentGroup: ChangeGroup | null = null;
  let lineNumber = 1;

  for (const change of diff) {
    const isChanged = change.added || change.removed;
    const lineCount = change.count ?? 1;

    if (!currentGroup || currentGroup.type !== (isChanged ? 'changed' : 'unchanged')) {
      if (currentGroup) {
        groups.push(currentGroup);
      }

      currentGroup = {
        type: isChanged ? 'changed' : 'unchanged',
        changes: [change],
        startLine: lineNumber,
        endLine: lineNumber + lineCount - 1,
        isCollapsible: false,
        isExpanded: false,
      };
    } else {
      currentGroup.changes.push(change);
      currentGroup.endLine = lineNumber + lineCount - 1;
    }

    lineNumber += lineCount;
  }

  if (currentGroup) {
    groups.push(currentGroup);
  }

  return groups.map((group) => {
    if (group.type === 'unchanged') {
      const totalLines = group.changes.reduce((sum, change) => sum + (change.count ?? 1), 0);
      return {
        ...group,
        isCollapsible: totalLines > contextLines,
        isExpanded: false,
      };
    }
    return group;
  });
}

/** Label for expand-unchanged-lines control (unified and split views). */
export function expandUnchangedLinesLabel(hiddenCount: number, position: CollapseExpandPosition): string {
  const positionText = position === 'above' ? 'above' : position === 'below' ? 'below' : '';
  const suffix = positionText ? ` ${positionText}` : '';
  return `Expand ${hiddenCount} unchanged line${hiddenCount !== 1 ? 's' : ''}${suffix}`;
}

/**
 * Ordered steps to render a collapsible diff: full or partial renders interleaved with expand affordances.
 */
export function buildCollapsibleDiffPlan(
  groups: ChangeGroup[],
  expandedGroups: Set<number>,
  contextLines: number,
): CollapsePlanStep[] {
  const steps: CollapsePlanStep[] = [];
  let currentLineNumber = 1;
  const firstCollapsibleIndex = groups.findIndex((g) => g.isCollapsible);
  const lastCollapsibleIndex = groups.findLastIndex((g) => g.isCollapsible);

  groups.forEach((group, groupIndex) => {
    const isExpanded = expandedGroups.has(groupIndex);
    const groupLineCount = group.changes.reduce((sum, change) => sum + (change.count ?? 1), 0);

    if (group.type === 'changed' || !group.isCollapsible || isExpanded) {
      steps.push({ kind: 'render', changes: group.changes, startLine: currentLineNumber });
    } else {
      const isFirst = groupIndex === firstCollapsibleIndex;
      const isLast = groupIndex === lastCollapsibleIndex;

      if (isFirst) {
        const hiddenCount = groupLineCount - contextLines;
        if (hiddenCount > 0) {
          steps.push({ kind: 'expand', hiddenCount, position: 'above', groupIndex });
          const lastChange = group.changes[group.changes.length - 1];
          steps.push({
            kind: 'render',
            changes: [
              {
                ...lastChange,
                value: lastChange.value.split('\n').slice(-contextLines).join('\n'),
              },
            ],
            startLine: currentLineNumber + groupLineCount - contextLines,
          });
        } else {
          steps.push({ kind: 'render', changes: group.changes, startLine: currentLineNumber });
        }
      } else if (isLast) {
        const hiddenCount = groupLineCount - contextLines;
        if (hiddenCount > 0) {
          const firstChange = group.changes[0];
          steps.push({
            kind: 'render',
            changes: [
              {
                ...firstChange,
                value: firstChange.value.split('\n').slice(0, contextLines).join('\n'),
              },
            ],
            startLine: currentLineNumber,
          });
          steps.push({ kind: 'expand', hiddenCount, position: 'below', groupIndex });
        } else {
          steps.push({ kind: 'render', changes: group.changes, startLine: currentLineNumber });
        }
      } else {
        const hiddenCount = groupLineCount - contextLines * 2;
        if (hiddenCount > 0) {
          const firstChange = group.changes[0];
          steps.push({
            kind: 'render',
            changes: [
              {
                ...firstChange,
                value: firstChange.value.split('\n').slice(0, contextLines).join('\n'),
              },
            ],
            startLine: currentLineNumber,
          });
          steps.push({ kind: 'expand', hiddenCount, position: 'middle', groupIndex });
          const lastChange = group.changes[group.changes.length - 1];
          steps.push({
            kind: 'render',
            changes: [
              {
                ...lastChange,
                value: lastChange.value.split('\n').slice(-contextLines).join('\n'),
              },
            ],
            startLine: currentLineNumber + groupLineCount - contextLines,
          });
        } else {
          steps.push({ kind: 'render', changes: group.changes, startLine: currentLineNumber });
        }
      }
    }

    currentLineNumber += groupLineCount;
  });

  return steps;
}
